import {
  Button,
  Flex,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  useDisclosure,
} from "@chakra-ui/react";
import { RiAddLine } from "@remixicon/react";
import React, {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  useMemo,
  useRef,
} from "react";

import MotionBox from "@/common/components/MotionBox";

import {
  MultiSelector,
  MultiSelectorBaseProps,
  MultiSelectorItem,
} from "./MultiSelector";

function contains(parent: HTMLElement | null, child: HTMLElement | null) {
  return parent === child || parent?.contains(child);
}

function getRelatedTarget(event: React.FocusEvent) {
  const activeEl = event.currentTarget.ownerDocument.activeElement;
  return (event.relatedTarget ?? activeEl) as HTMLElement | null;
}

export type MultiSelectorPopoverProps<T extends MultiSelectorItem> =
  MultiSelectorBaseProps<T> & {
    children?: ReactElement | ReactElement[];
    isReadOnly?: boolean;
    allowReadOnlyOpen?: boolean;
    addLabel?: string;
  };

export const MultiSelectorPopover = <T extends MultiSelectorItem>({
  children,
  isReadOnly,
  allowReadOnlyOpen = false,
  addLabel = "Add",
  ...rest
}: MultiSelectorPopoverProps<T>) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerWrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, onToggle, onClose } = useDisclosure();
  const canOpen = !isReadOnly || allowReadOnlyOpen;

  const triggers = useMemo(
    () =>
      Children.map(children, (child) => {
        if (canOpen && isValidElement(child)) {
          return cloneElement(child, {
            cursor: "pointer",
            onClick: onToggle,
          } as any);
        }
        return child;
      }),
    [canOpen, children, onToggle],
  );

  return (
    <Popover
      closeOnBlur={false}
      initialFocusRef={searchInputRef}
      isOpen={isOpen}
      placement="bottom-start"
      isLazy
      onClose={onClose}
      {...rest}
    >
      <PopoverAnchor>
        <Flex ref={triggerWrapperRef} gap={2} wrap="wrap">
          {triggers}
          {!isReadOnly && (
            <MotionBox transition={{ duration: 0.1 }} layout>
              <Button
                aria-label={addLabel}
                color="dimmed"
                leftIcon={<RiAddLine size={16} />}
                pl={1}
                variant="ghost"
                onClick={onToggle}
              >
                {addLabel}
              </Button>
            </MotionBox>
          )}
        </Flex>
      </PopoverAnchor>
      <PopoverContent
        ref={popoverRef}
        w="auto"
        onBlur={(e) => {
          const relatedTarget = getRelatedTarget(e);
          const targetIsPopover = contains(popoverRef.current, relatedTarget);
          const targetIsTrigger = contains(
            triggerWrapperRef.current,
            relatedTarget,
          );

          const isValidBlur = !targetIsPopover && !targetIsTrigger;
          if (isOpen && isValidBlur) {
            onClose();
          }
        }}
      >
        <PopoverBody display="flex" flexDirection="column" p={0}>
          <MultiSelector
            ref={searchInputRef}
            borderBottom="none"
            isReadOnly={isReadOnly}
            pt={3}
            {...rest}
          />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
